import React, { useState, useEffect } from 'react';
import { Menu } from 'lucide-react';
function CountDown() {
 const [timeRemaining, setTimeRemaining] = useState({
        days: '000',
        hours: '00',
        minutes: '00',
        seconds: '00',
        ddOffset: 440,
        hhOffset: 440,
        mmOffset: 440,
        ssOffset: 440,
    });
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // Raio do círculo SVG é 70, então a circunferência (2 * pi * r) é ~439.8. Usamos 440.
    const CIRCUMFERENCE = 440;
    /**
     * Função para calcular o deslocamento do traço para a animação do círculo.
     * @param {number} value - O valor atual (dias, horas, minutos, segundos).
     * @param {number} max - O valor máximo para esta unidade de tempo (ex: 24 para horas).
     * @returns {number} O valor do stroke-dashoffset.
     */
    const calculateOffset = (value, max) => {
        // Garantir que o valor não seja negativo e esteja dentro do intervalo [0, max]
        const boundedValue = Math.max(0, Math.min(value, max));
        return CIRCUMFERENCE - (boundedValue / max) * CIRCUMFERENCE;
    };

    /**
     * Lógica principal do contador regressivo
     */
    useEffect(() => {
        // Define o alvo: 1º de Janeiro do próximo ano
        const now = new Date();
        const nextYear = now.getFullYear();
        const newYearDate = new Date(`November 18, ${nextYear} 00:00:00`).getTime();

        const updateCountdown = () => {
            const now = new Date().getTime();
            const distance = newYearDate - now;

            if (distance < 0) {
                // Se o tempo acabar
                setTimeRemaining({
                    days: '000', hours: '00', minutes: '00', seconds: '00',
                    ddOffset: 440, hhOffset: 440, mmOffset: 440, ssOffset: 440,
                });
                return;
            }

            // Cálculos de tempo
            const days = Math.floor(distance / (1000 * 60 * 60 * 24));
            const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((distance % (1000 * 60)) / 1000);

            // Determinar o máximo para o cálculo do offset do dia (dias restantes no ano)
            const daysInYear = new Date(nextYear, 0, 1) - new Date(nextYear - 1, 0, 1);
            const maxDays = Math.ceil(daysInYear / (1000 * 60 * 60 * 24)); // Total de dias no ano

            setTimeRemaining({
                // Formatação
                days: days.toString().padStart(3, '0'),
                hours: hours.toString().padStart(2, '0'),
                minutes: minutes.toString().padStart(2, '0'),
                seconds: seconds.toString().padStart(2, '0'),

                // Cálculo do Offset da Animação. Usamos maxDays apenas para o primeiro cálculo visual,
                // mas 24/60/60 para os outros.
                ddOffset: calculateOffset(days, maxDays),
                hhOffset: calculateOffset(hours, 24),
                mmOffset: calculateOffset(minutes, 60),
                ssOffset: calculateOffset(seconds, 60),
            });
        };

        // Inicia e atualiza a cada segundo
        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);

        // Limpeza do intervalo
        return () => clearInterval(interval);
    }, []);

    // Oculta a barra de rolagem horizontal se o menu estiver aberto, melhorando a experiência mobile
    useEffect(() => {
        document.body.style.overflowX = isMenuOpen ? 'hidden' : 'auto';
    }, [isMenuOpen]);

const componentStyles = `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap');
        #countdown {
            display: flex;
            gap: 1rem;
            flex-wrap: wrap;
            justify-content: center;
        }

        #countdown .circle {
        position: relative;
        max-width: 140px;
        max-height: 140px;
        display: flex;
        justify-content: center;
        align-items: center;
        background: rgba(0, 0, 0, 0.1);
        border-radius: 50%;
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
        margin: 10px;
        }

        #countdown .circle div {
            position: absolute;
            text-align: center;
            font-size: 2.5rem;
            font-weight: 600;
            line-height: 1.2;
            color: #fff;
            letter-spacing: 1px;
            text-shadow: 0 0 10px var(--clr); /* Efeito neon sutil */
        }

        .circle span {
            font-size: 0.75rem;
            font-weight: 300;
            display: block;
            text-transform: uppercase;
            color: #ccc;
        }

        .circle svg {
            width: 100%;
            height: 100%;
            transform: rotate(270deg); 
            overflow: visible; 
            transform: translate(5px,4px);
        }

        .circle svg circle {
            width: 100%;
            height: 100%;
            fill: transparent;
            stroke-width: 8;
        }

        .circle svg circle:nth-child(1) {
            stroke: #2c2c2c;
        }

        .circle svg circle:nth-child(2) {
            stroke: var(--clr);
            stroke-dasharray: 440; /* Circunferência de r=70 (440) */
            stroke-dashoffset: 440;
            transition: stroke-dashoffset 1s linear; /* Transição suave */
            box-shadow: 0 0 20px var(--clr);
        }

        .shareMenu, .socialMenu {
            list-style: none;
            padding: 0;
            margin: 0;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 1rem;
        }

        .shareMenu li, .socialMenu li {
            position: relative;
            background: #2c2c2c;
            width: 50px;
            height: 50px;
            border-radius: 50%;
            display: flex;
            justify-content: center;
            align-items: center;
            transition: background 0.3s ease, transform 0.3s ease;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4);
        }

        .shareMenu li span, .socialMenu li span {
            position: absolute;
            left: -100px;
            background: #333;
            color: #fff;
            padding: 0.25rem 0.75rem;
            border-radius: 5px;
            font-size: 0.8rem;
            white-space: nowrap;
            opacity: 0;
            transition: opacity 0.3s ease;
            pointer-events: none;
        }

        @media screen and (max-width:1024px){
            #countdown {
                gap: 0rem;
                flex-wrap: nowrap;
            }
        }

        @media (max-width: 768px) {
            #countdown .circle div {
            font-size: 1rem;
            }
            .circle {
                width: 120px !important; 
                height: 120px !important;
                margin: 5px;
            }
           .circle svg { 
            transform: translate(2px,2px);
             }
            .circle div {
             font-size: 2rem; 
            }
            .text-logo .main-title {
            font-size: 30px !important;
          }
            .event-location{
            font-size: 0.8rem;
            }
        }

        @media (max-width: 540px) {
        .circle svg {
            width: 80px;
            height: 100%;
        }
        .circle span {
        font-size: 0.45rem;
        font-weight: 500;
        }
        .circle {
            width: 60px !important;
            height: 60px !important;
        }
        }
    `;
  return (
            <div className="countdown-event">
            <style>{componentStyles}</style>
            <div className="container">
                <div id="countdown" className="flex  justify-center">
                    {/* Dia */}
                    <div className="circle" style={{'--clr': '#0168b1'}}>
                        <svg viewBox="0 0 150 150" preserveAspectRatio="xMidYMid meet">
                            <circle cx="70" cy="70" r="70"></circle>
                            <circle cx="70" cy="70" r="70"
                                    style={{ strokeDashoffset: timeRemaining.ddOffset }} ></circle>
                        </svg>
                        <div id="days">{timeRemaining.days}<br /><span>Dias</span></div>
                    </div>

                    {/* Hora */}
                    <div className="circle" style={{'--clr': 'white'}}>
                        <svg viewBox="0 0 150 150">
                            <circle cx="70" cy="70" r="70"></circle>
                            <circle cx="70" cy="70" r="70"
                                    style={{ strokeDashoffset: timeRemaining.hhOffset }}
                            ></circle>
                        </svg>
                        <div id="hours">{timeRemaining.hours}<br /><span>Horas</span></div>
                    </div>

                    {/* Minuto */}
                    <div className="circle" style={{'--clr': '#033a71'}}>
                         <svg viewBox="0 0 150 150">
                            <circle cx="70" cy="70" r="70"></circle>
                            <circle cx="70" cy="70" r="70"
                                    style={{ strokeDashoffset: timeRemaining.mmOffset }}
                            ></circle>
                        </svg>
                        <div id="minutes">{timeRemaining.minutes}<br /><span>Minutos</span></div>
                    </div>

                    {/* Segundo */}
                    <div className="circle" style={{'--clr': '#b0e0e6'}}>
                          <svg viewBox="0 0 150 150">
                            <circle cx="70" cy="70" r="70"></circle>
                            <circle cx="70" cy="70" r="70"
                                    style={{ strokeDashoffset: timeRemaining.ssOffset }}
                            ></circle>
                        </svg>
                        <div id="seconds">{timeRemaining.seconds}<br /><span>Segundos</span></div>
                    </div>
                </div>
            </div>
        </div>
  )
}

export default CountDown